import isEqual from 'fast-deep-equal';
import { batch } from 'solid-js';
import {
  produce,
  reconcile,
  unwrap,
  type SetStoreFunction,
} from 'solid-js/store';
import type { Writable } from 'ts-essentials';
import {
  GeospatialFeature,
  MapWorkspace,
  MapLayer,
  PropertyValue,
  FEATURE_MEMORY_BOUND_METERS,
  findFeatureIdsBeyondMemoryBound,
  type FeatureRetentionRegion,
  type ScenePoint,
} from '@trimble/geospatial';

export type FeatureRetentionRegionReader = () => FeatureRetentionRegion;

export interface FeatureExtractionState {
  initialized: boolean;
  workspaces: MapWorkspace[];
  layers: MapLayer[];
  selectedLayerId: string | undefined;
  selectedFeature: GeospatialFeature | undefined;
  features: Record<string, GeospatialFeature>;
  sceneAnchors: Record<string, ScenePoint>;
  visibleLayerIds: Record<string, true>;
}

export interface FeatureExtractionLoadedLayers {
  workspaces: MapWorkspace[];
  layers: MapLayer[];
  defaultLayerId: string | undefined;
}

export interface FeatureExtractionStateModel {
  upsertFeatures(features: readonly GeospatialFeature[]): void;
  upsertFeature(feature: GeospatialFeature): void;
  mergeFeatureProperties(
    featureId: string,
    values: Readonly<Record<string, PropertyValue>>
  ): void;
  getSceneAnchors(): Readonly<Record<string, ScenePoint>>;
  setSceneAnchors(anchors: Readonly<Record<string, ScenePoint>>): void;
  /** Keeps features near this camera resident; call the result to drop the claim. */
  registerFeatureRetention(read: FeatureRetentionRegionReader): () => void;
  evictDistantFeatures(): void;
  removeFeature(featureId: string): void;
  isLayerVisible(layerId: string): boolean;
  toggleLayerVisibility(layerId: string): void;
  setVisibleLayerIds(ids: readonly string[]): void;
  applyProjectLayers(data: FeatureExtractionLoadedLayers): void;
}

export interface CreateFeatureExtractionStateModelParams {
  readonly editingState: FeatureExtractionState;
  readonly setEditingState: SetStoreFunction<FeatureExtractionState>;
  bumpTileCacheToken(): void;
}

export function createDefaultFeatureExtractionState(): FeatureExtractionState {
  return {
    initialized: false,
    workspaces: [],
    layers: [],
    selectedLayerId: undefined,
    selectedFeature: undefined,
    features: {},
    sceneAnchors: {},
    visibleLayerIds: {},
  };
}

export function createFeatureExtractionStateModel(
  params: CreateFeatureExtractionStateModelParams
): FeatureExtractionStateModel {
  const { editingState, setEditingState, bumpTileCacheToken } = params;

  const upsertFeatures = (incoming: readonly GeospatialFeature[]): void => {
    const merged = new Map<string, GeospatialFeature & { id: string }>();
    for (const feature of incoming) {
      if (typeof feature.id !== 'string' || feature.id.length === 0) {
        continue;
      }
      const base = merged.get(feature.id) ?? editingState.features[feature.id];
      merged.set(
        feature.id,
        base
          ? {
              ...feature,
              id: feature.id,
              properties: { ...base.properties, ...feature.properties },
              metadata: { ...base.metadata, ...feature.metadata },
            }
          : { ...feature, id: feature.id }
      );
    }
    // Every pane's tile loader ingests the same features; rewriting an
    // identical one re-runs each pane's CAD mirror over the whole store.
    const changed = [...merged.values()].filter((feature) => {
      const existing = editingState.features[feature.id];
      return !existing || !isEqual(unwrap(existing), feature);
    });
    if (changed.length === 0) {
      return;
    }
    setEditingState(
      'features',
      produce((features) => {
        for (const feature of changed) {
          features[feature.id] = feature;
        }
      })
    );
  };

  const upsertFeature = (feature: GeospatialFeature): void => {
    upsertFeatures([feature]);
  };

  /**
   * Upserting a selection-derived feature would write its scene-space geometry
   * into the WGS-84 map; merging in place also keeps selectedFeature identity
   * so keyed UI stays mounted.
   */
  const mergeFeatureProperties = (
    featureId: string,
    values: Readonly<Record<string, PropertyValue>>
  ): void => {
    const mergeInto = (feature: GeospatialFeature): void => {
      // produce already owns a mutable copy of the readonly feature.
      (feature as Writable<GeospatialFeature>).properties = {
        ...feature.properties,
        ...values,
      };
    };

    setEditingState(
      produce((state) => {
        const feature = state.features[featureId];
        if (feature) {
          mergeInto(feature);
        }
        if (state.selectedFeature?.id === featureId) {
          mergeInto(state.selectedFeature);
        }
      })
    );
  };

  const getSceneAnchors = (): Readonly<Record<string, ScenePoint>> =>
    editingState.sceneAnchors;

  const setSceneAnchors = (
    anchors: Readonly<Record<string, ScenePoint>>
  ): void => {
    if (Object.keys(anchors).length === 0) {
      return;
    }
    setEditingState(
      'sceneAnchors',
      produce((sceneAnchors) => {
        for (const [featureId, anchor] of Object.entries(anchors)) {
          sceneAnchors[featureId] = anchor;
        }
      })
    );
  };

  const retentionRegions = new Set<FeatureRetentionRegionReader>();

  const registerFeatureRetention = (
    read: FeatureRetentionRegionReader
  ): (() => void) => {
    retentionRegions.add(read);
    return () => {
      retentionRegions.delete(read);
    };
  };

  const evictDistantFeatures = (): void => {
    const evictedIds = findFeatureIdsBeyondMemoryBound(
      editingState.sceneAnchors,
      Array.from(retentionRegions, (read) => read()),
      FEATURE_MEMORY_BOUND_METERS
    );
    if (evictedIds.length === 0) {
      return;
    }
    setEditingState(
      produce((state) => {
        for (const featureId of evictedIds) {
          delete state.features[featureId];
          delete state.sceneAnchors[featureId];
          if (state.selectedFeature?.id === featureId) {
            state.selectedFeature = undefined;
          }
        }
      })
    );
  };

  const removeFeature = (featureId: string): void => {
    if (!(featureId in editingState.features)) {
      return;
    }
    batch(() => {
      setEditingState(
        produce((state) => {
          delete state.features[featureId];
          delete state.sceneAnchors[featureId];
        })
      );
      bumpTileCacheToken();
    });
  };

  const isLayerVisible = (layerId: string): boolean =>
    editingState.visibleLayerIds[layerId] === true;

  const toggleLayerVisibility = (layerId: string): void => {
    const isVisible = editingState.visibleLayerIds[layerId] === true;
    setEditingState(
      'visibleLayerIds',
      produce((visibleLayerIds) => {
        if (isVisible) {
          delete visibleLayerIds[layerId];
        } else {
          visibleLayerIds[layerId] = true;
        }
      })
    );
  };

  const setVisibleLayerIds = (ids: readonly string[]): void => {
    setEditingState(
      'visibleLayerIds',
      reconcile(Object.fromEntries(ids.map((id) => [id, true] as const)))
    );
  };

  const applyProjectLayers = (data: FeatureExtractionLoadedLayers): void => {
    const previousLayerIds = new Set(
      editingState.layers.map((layer) => layer.id)
    );
    const visibleLayerIds = Object.fromEntries(
      data.layers
        .filter(
          (layer) =>
            !previousLayerIds.has(layer.id) ||
            editingState.visibleLayerIds[layer.id] === true
        )
        .map((layer) => [layer.id, true] as const)
    );

    const currentSelectedId = editingState.selectedLayerId;
    const selectedStillValid =
      currentSelectedId !== undefined &&
      data.layers.some((layer) => layer.id === currentSelectedId);

    setEditingState({
      initialized: true,
      workspaces: data.workspaces,
      layers: data.layers,
      visibleLayerIds,
      selectedLayerId: selectedStillValid
        ? currentSelectedId
        : data.defaultLayerId,
    });
  };

  return {
    upsertFeatures,
    upsertFeature,
    mergeFeatureProperties,
    getSceneAnchors,
    setSceneAnchors,
    registerFeatureRetention,
    evictDistantFeatures,
    removeFeature,
    isLayerVisible,
    toggleLayerVisibility,
    setVisibleLayerIds,
    applyProjectLayers,
  };
}
